import {
  ShortTextOutlined, NotesOutlined, AlternateEmailOutlined, PhoneOutlined,
  ArrowDropDownCircleOutlined, RadioButtonCheckedOutlined, CheckBoxOutlined,
  ToggleOnOutlined, CalendarTodayOutlined, AttachFileOutlined, StarOutlined,
  LooksOneOutlined, LinearScaleOutlined, TitleOutlined, HorizontalRuleOutlined,
  ViewStreamOutlined, SplitscreenOutlined, ImageOutlined, CreditCardOutlined,
  GridViewOutlined, ViewColumnOutlined, BadgeOutlined,
} from '@mui/icons-material';
import React from 'react';

// ── Accent colours ────────────────────────────────────────────────────────────
export const FIELD_COLOR     = '#1075BA';
export const STEP_COLOR      = '#7c3aed';
export const SUCCESS_COLOR   = '#059669';
export const SELECTION_COLOR = '#d97706';

// ── Form-type colour map ──────────────────────────────────────────────────────
export const FORM_TYPE_COLORS = {
  registration: '#1075BA',
  survey:       '#7c3aed',
  feedback:     '#059669',
  waitlist:     '#d97706',
  application:  '#dc2626',
  quiz:         '#0891b2',
  contact:      '#be185d',
  other:        '#64748b',
};

// ── Status dot colours ────────────────────────────────────────────────────────
export const STATUS_DOT = {
  published: '#22c55e',
  draft:     '#f59e0b',
  closed:    '#94a3b8',
  archived:  '#cbd5e1',
};

// ── Field type registry ───────────────────────────────────────────────────────
export const FIELD_TYPES = {
  // Input
  firstName: { label: 'First Name',  icon: <BadgeOutlined sx={{ fontSize: 16 }} />,               group: 'Input',  defaultData: { type: 'firstName', label: 'First Name',            placeholder: 'Enter your first name',     required: false, helperText: '' } },
  lastName:  { label: 'Last Name',   icon: <BadgeOutlined sx={{ fontSize: 16 }} />,               group: 'Input',  defaultData: { type: 'lastName',  label: 'Last Name',             placeholder: 'Enter your last name',      required: false, helperText: '' } },
  email:     { label: 'Email',       icon: <AlternateEmailOutlined sx={{ fontSize: 16 }} />,      group: 'Input',  defaultData: { type: 'email',     label: 'Email Address',         placeholder: 'you@example.com',           required: false, helperText: '' } },
  phone:     { label: 'Phone',       icon: <PhoneOutlined sx={{ fontSize: 16 }} />,               group: 'Input',  defaultData: { type: 'phone',     label: 'Phone Number',          placeholder: '+1 (555) 000-0000',         required: false, helperText: '' } },
  text:      { label: 'Short Text',  icon: <ShortTextOutlined sx={{ fontSize: 16 }} />,          group: 'Input',  defaultData: { type: 'text',      label: 'Short Answer',         placeholder: '',                  required: false, helperText: '' } },
  textarea:  { label: 'Long Text',   icon: <NotesOutlined sx={{ fontSize: 16 }} />,               group: 'Input',  defaultData: { type: 'textarea',  label: 'Long Answer',           placeholder: '',                  required: false, helperText: '', rows: 4 } },
  number:    { label: 'Number',      icon: <LooksOneOutlined sx={{ fontSize: 16 }} />,            group: 'Input',  defaultData: { type: 'number',    label: 'Number',                placeholder: '',                  required: false, helperText: '', min: '', max: '' } },
  // Choice
  select:   { label: 'Dropdown',    icon: <ArrowDropDownCircleOutlined sx={{ fontSize: 16 }} />, group: 'Choice', defaultData: { type: 'select',   label: 'Select an option',      required: false, helperText: '', options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }] } },
  radio:    { label: 'Radio',       icon: <RadioButtonCheckedOutlined sx={{ fontSize: 16 }} />,  group: 'Choice', defaultData: { type: 'radio',    label: 'Choose one',            required: false, helperText: '', options: [{ label: 'Option 1', value: 'option_1' }, { label: 'Option 2', value: 'option_2' }] } },
  checkbox: { label: 'Checkbox',    icon: <CheckBoxOutlined sx={{ fontSize: 16 }} />,            group: 'Choice', defaultData: { type: 'checkbox', label: 'Select all that apply', required: false, helperText: '', options: [{ label: 'Option 1', value: 'option_1' }] } },
  toggle:   { label: 'Toggle',      icon: <ToggleOnOutlined sx={{ fontSize: 16 }} />,            group: 'Choice', defaultData: { type: 'toggle',   label: 'I agree',               required: false, helperText: '', defaultValue: false } },
  // ── Selection Group — inline card picker living inside a fields step ──────
  selection_group: {
    label: 'Selection Group',
    icon:  <GridViewOutlined sx={{ fontSize: 16 }} />,
    group: 'Choice',
    defaultData: {
      type:          'selection_group',
      label:         'Select an option',
      helperText:    '',
      required:      true,
      selectionMode: 'single',   // 'single' | 'multi'
      columns:       3,
      badge:         '',
      subtitle:      '',
      items: [
        { id: '__item_1__', title: 'Option 1', description: '', icon: null, price: null, currency: 'PHP', priceLabel: 'Free',  available: true, order: 0 },
        { id: '__item_2__', title: 'Option 2', description: '', icon: null, price: null, currency: 'PHP', priceLabel: null,    available: true, order: 1 },
      ],
    },
  },
  // ── Column Group — multi-column layout container ──────────────────────────
  column_group: {
    label: 'Columns',
    icon:  <ViewColumnOutlined sx={{ fontSize: 16 }} />,
    group: 'Layout',
    defaultData: {
      type:    'column_group',
      label:   '',
      columns: [
        { id: '__col_1__', fields: [] },
        { id: '__col_2__', fields: [] },
      ],
    },
  },
  // Other
  date:     { label: 'Date',        icon: <CalendarTodayOutlined sx={{ fontSize: 16 }} />,       group: 'Other',  defaultData: { type: 'date',     label: 'Date',                  required: false, helperText: '' } },
  file:     { label: 'File Upload', icon: <AttachFileOutlined sx={{ fontSize: 16 }} />,          group: 'Other',  defaultData: { type: 'file',     label: 'Upload File',           required: false, helperText: '', accept: '', maxSizeMb: 5 } },
  rating:   { label: 'Rating',      icon: <StarOutlined sx={{ fontSize: 16 }} />,                group: 'Other',  defaultData: { type: 'rating',   label: 'Rate your experience',  required: false, helperText: '', max: 5 } },
  scale:    { label: 'Scale',       icon: <LinearScaleOutlined sx={{ fontSize: 16 }} />,         group: 'Other',  defaultData: { type: 'scale',    label: 'On a scale of 1–10',    required: false, helperText: '', min: 1, max: 10, step: 1 } },
  // Layout
  heading:  { label: 'Heading',     icon: <TitleOutlined sx={{ fontSize: 16 }} />,               group: 'Layout', defaultData: { type: 'heading',  label: '',  content: 'Section Heading',     level: 'h3' } },
  divider:  { label: 'Divider',     icon: <HorizontalRuleOutlined sx={{ fontSize: 16 }} />,      group: 'Layout', defaultData: { type: 'divider',  label: '' } },
};

export const FIELD_GROUPS = ['Input', 'Choice', 'Other', 'Layout'];

// ── Canvas layout registry ────────────────────────────────────────────────────
export const LAYOUTS = [
  { value: 'image-top',   label: 'Image Top',   icon: <ViewStreamOutlined sx={{ fontSize: 17 }} /> },
  { value: 'image-left',  label: 'Image Left',  icon: <SplitscreenOutlined sx={{ fontSize: 17, transform: 'rotate(90deg)' }} /> },
  { value: 'image-right', label: 'Image Right', icon: <SplitscreenOutlined sx={{ fontSize: 17, transform: 'rotate(-90deg)' }} /> },
  { value: 'image-bg',    label: 'Full BG',     icon: <ImageOutlined sx={{ fontSize: 17 }} /> },
  { value: 'card-left',   label: 'Card Left',   icon: <CreditCardOutlined sx={{ fontSize: 17 }} /> },
  { value: 'card-right',  label: 'Card Right',  icon: <CreditCardOutlined sx={{ fontSize: 17, transform: 'scaleX(-1)' }} /> },
];

// ── Form types for CreateFormDialog ──────────────────────────────────────────
export const FORM_TYPES = [
  { value: 'registration', label: 'Registration', desc: 'Collect event sign-ups and participant info' },
  { value: 'survey',       label: 'Survey',        desc: 'Gather opinions and feedback at scale' },
  { value: 'feedback',     label: 'Feedback',      desc: 'Post-event ratings and comments' },
  { value: 'waitlist',     label: 'Waitlist',      desc: 'Queue interested participants for a limited event' },
  { value: 'application',  label: 'Application',   desc: 'Structured submissions that need review' },
  { value: 'quiz',         label: 'Quiz',          desc: 'Test knowledge with scored responses' },
  { value: 'contact',      label: 'Contact',       desc: 'Simple message / inquiry form' },
  { value: 'other',        label: 'Other',         desc: 'Custom or uncategorized form' },
];